import { AppModal } from '@Common/AppModal';
import { Spinner } from '@Common/Spinner';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { defaultToastProps } from 'appConstants';
import { SetStateType } from 'commonTypes';
import { classNames } from 'helpers/validation';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import { useGetSubscription, useStartFreeTrial } from 'store/subscription/actions';
import { SubscriptionPriceType } from 'store/subscription/types';

import { BillingFreeTrialStep } from '../UI/BillingFreeTrialStep';
import { ChooseSubscriptionFreeTrial } from '../UI/ChooseSubscriptionFreeTrial';
import { PaymentFreeTrialStep } from '../UI/PaymentFreeTrialStep';
import { PreviewSubscriptionStep } from '../UI/PreviewSubscriptionStep';

interface StartFreeTrialModalProps {
  open: boolean;
  setOpen: SetStateType<boolean>;
}

export const StartFreeTrialModal = ({ open, setOpen }: StartFreeTrialModalProps) => {
  const { mutate: startFreeTrial, isPending: isStartFreeTrialPending } =
    useStartFreeTrial();
  const { data: subscription } = useGetSubscription();
  const [step, setStep] = useState(1);
  const [chosenPrice, setChosenPrice] = useState<SubscriptionPriceType | null>(null);

  const closeModal = () => {
    setOpen(false);
    setTimeout(() => {
      setStep(1);
      setChosenPrice(null);
    }, 250);
  };

  const handleButtonClick = () => {
    if (step === 3) {
      if (!chosenPrice) {
        toast('Select a subscription plan', {
          ...defaultToastProps,
          type: 'warning',
        });
        return;
      } else {
        setStep((prev) => prev + 1);
      }
    } else if (step === 4) {
      if (chosenPrice && subscription) {
        startFreeTrial(
          {
            customer: subscription.id,
            items: [
              {
                price: chosenPrice.price_id,
              },
            ],
          },
          {
            onSuccess: closeModal,
          },
        );
      }
    }
  };

  const returnNextButtonContent = () => {
    if (isStartFreeTrialPending) {
      return <Spinner color="bg-white" />;
    } else if (step === 1) {
      return <FormattedMessage id="next" />;
    } else if (step === 2) {
      return <FormattedMessage id="next" />;
    } else if (step === 3) {
      return <FormattedMessage id="next" />;
    } else if (step === 4) {
      return <FormattedMessage id="subscribe" />;
    }
  };

  return (
    <AppModal
      open={open}
      size={classNames('transition-all', step === 3 ? 'sm:max-w-6xl ' : 'sm:max-w-2xl ')}
    >
      <div className="flex flex-col transition-all">
        <div className="flex items-center justify-between w-full mb-3">
          <h1 className="text-xl">
            <FormattedMessage id="startFreeTrial" />
          </h1>
          <XMarkIcon
            onClick={() => {
              closeModal();
            }}
            className="h-6 w-6 cursor-pointer text-gray-500 md:hover:text-gray-700"
          />
        </div>

        {step === 1 && <BillingFreeTrialStep setStep={setStep} />}
        {step === 2 && <PaymentFreeTrialStep setStep={setStep} />}

        {step === 3 && (
          <ChooseSubscriptionFreeTrial
            chosenPrice={chosenPrice}
            setChosenPrice={setChosenPrice}
          />
        )}

        {step === 4 && chosenPrice && (
          <PreviewSubscriptionStep chosenPrice={chosenPrice} />
        )}

        {step !== 2 && step !== 1 && (
          <div className="w-full flex items-center justify-end space-x-3 mt-4">
            {step !== 1 && (
              <button
                onClick={() => setStep((prev) => prev - 1)}
                type="button"
                className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                <FormattedMessage id="previous" />
              </button>
            )}
            <button
              onClick={handleButtonClick}
              type="button"
              className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              {returnNextButtonContent()}
            </button>
          </div>
        )}
      </div>
    </AppModal>
  );
};
